/**
 * The shell: banner, header, routes.
 *
 * `/login` is public; everything else is behind a session and anything unmatched folds
 * back to `/`. The login path is fixed by the API, which redirects failed handshakes to
 * `/login?error=<code>` on `WEB_ORIGIN`.
 *
 * The three Order routes are wrapped individually rather than nested under one guarded
 * parent: `RequireSession` renders a redirect, and a nested layout would have to render
 * its chrome first — briefly showing an Orders page to someone who has no session.
 */

import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LOCALES, type Locale } from './i18n/index.js';
import { SessionProvider, useSession } from './auth/session.js';
import { RequireSession } from './auth/require-session.js';
import { Sidebar } from './nav/sidebar.js';
import { QuickJump } from './nav/jump.js';
import { currentScreen, reachableScreens } from './nav/screens.js';
import { PageHeader } from './components/page-header.js';
import { LoginPage } from './pages/login.js';
import { LandingPage } from './pages/landing.js';
import { RequirePlatformStaff } from './auth/require-platform-staff.js';
import { RequireOpsFacet } from './auth/require-ops-facet.js';
import { RequireAccessAuthority } from './auth/require-access-authority.js';
import { InboxBell } from './pages/inbox-bell.js';

/**
 * Every screen but three is its own chunk — slice 8, F20.
 *
 * One 1,215.78 kB chunk held all thirty-nine routes, so a dispatcher who only ever opens the board
 * downloaded the tariff editor, the gallery and every configuration form to get there. It was the
 * component layer that made this worth doing rather than circular: before slice 2 there were fifty-three
 * page-to-page imports — `ops-config-policy.tsx` was a 581-line *route* that six other routes imported
 * for `Outcome`, `Ownership` and `refusalLines` — so a chunk per route would mostly have contained
 * other routes. The primitives are what each chunk shares now.
 *
 * ## The three that stay static, each for its own reason
 *
 * - **`LoginPage`**, because the API redirects a *failed* handshake to `/login?error=<code>`. Fetching
 *   a chunk to render the screen that explains a failure is one more thing to fail at the moment
 *   least able to absorb it.
 * - **`LandingPage`**, the first screen after sign-in: lazy, it would add a round trip to the paint a
 *   reader is already waiting on.
 * - **`InboxBell`**, which is header furniture on every authenticated page rather than a route.
 *
 * `lazy()` wants a module whose `default` is the component and these are named exports, so each entry
 * maps one to the other. The alternative — adding a default export to thirty-three pages — would put a
 * second name on every one of them for a bundler’s convenience.
 */
const OrdersListPage = lazy(async () => ({ default: (await import('./pages/orders-list.js')).OrdersListPage }));
const OrderNewPage = lazy(async () => ({ default: (await import('./pages/order-new.js')).OrderNewPage }));
const OrderDetailPage = lazy(async () => ({ default: (await import('./pages/order-detail.js')).OrderDetailPage }));
const StatementPage = lazy(async () => ({ default: (await import('./pages/statement.js')).StatementPage }));
const MeNotificationsPage = lazy(async () => ({
  default: (await import('./pages/me-notifications.js')).MeNotificationsPage,
}));
const OpsBoardPage = lazy(async () => ({ default: (await import('./pages/ops-board.js')).OpsBoardPage }));
const OpsAuditPage = lazy(async () => ({ default: (await import('./pages/ops-audit.js')).OpsAuditPage }));
const OpsCredentialsPage = lazy(async () => ({ default: (await import('./pages/ops-credentials.js')).OpsCredentialsPage }));
const OpsExceptionsPage = lazy(async () => ({ default: (await import('./pages/ops-exceptions.js')).OpsExceptionsPage }));
const OpsSlaBreachesPage = lazy(async () => ({ default: (await import('./pages/ops-sla-breaches.js')).OpsSlaBreachesPage }));
const OpsCompliancePage = lazy(async () => ({ default: (await import('./pages/ops-compliance.js')).OpsCompliancePage }));
const OrderLabelsPage = lazy(async () => ({ default: (await import('./pages/order-labels.js')).OrderLabelsPage }));
const OpsCouriersPage = lazy(async () => ({ default: (await import('./pages/ops-couriers.js')).OpsCouriersPage }));
const OpsDispatchPage = lazy(async () => ({ default: (await import('./pages/ops-dispatch.js')).OpsDispatchPage }));
const OpsShipmentDetailPage = lazy(async () => ({ default: (await import('./pages/ops-shipment-detail.js')).OpsShipmentDetailPage }));
const OpsConfigPolicyPage = lazy(async () => ({ default: (await import('./pages/ops-config-policy.js')).OpsConfigPolicyPage }));
const OpsConfigFxPage = lazy(async () => ({ default: (await import('./pages/ops-config-fx.js')).OpsConfigFxPage }));
const OpsConfigVatPage = lazy(async () => ({ default: (await import('./pages/ops-config-vat.js')).OpsConfigVatPage }));
const OpsConfigVatRegistrationPage = lazy(async () => ({ default: (await import('./pages/ops-config-vat-registration.js')).OpsConfigVatRegistrationPage }));
const OpsConfigCodCustodyLicencePage = lazy(async () => ({ default: (await import('./pages/ops-config-cod-custody-licence.js')).OpsConfigCodCustodyLicencePage }));
const OpsConfigScreeningValidityPage = lazy(async () => ({ default: (await import('./pages/ops-config-screening-validity.js')).OpsConfigScreeningValidityPage }));
const OpsConfigRemittanceRailPage = lazy(async () => ({ default: (await import('./pages/ops-config-remittance-rail.js')).OpsConfigRemittanceRailPage }));
const OpsNotificationsPage = lazy(async () => ({ default: (await import('./pages/ops-notifications.js')).OpsNotificationsPage }));
const OpsNotificationSendsPage = lazy(async () => ({ default: (await import('./pages/ops-notification-sends.js')).OpsNotificationSendsPage }));
const OpsConfigScreeningPolicyPage = lazy(async () => ({ default: (await import('./pages/ops-config-screening-policy.js')).OpsConfigScreeningPolicyPage }));
const OpsConfigTariffsPage = lazy(async () => ({ default: (await import('./pages/ops-config-tariffs.js')).OpsConfigTariffsPage }));
const OpsConfigTariffEditorPage = lazy(async () => ({ default: (await import('./pages/ops-config-tariff-editor.js')).OpsConfigTariffEditorPage }));
const OpsGeographyPage = lazy(async () => ({ default: (await import('./pages/ops-geography.js')).OpsGeographyPage }));
const OpsCataloguePage = lazy(async () => ({ default: (await import('./pages/ops-catalogue.js')).OpsCataloguePage }));
const OpsDirectoryPage = lazy(async () => ({ default: (await import('./pages/ops-directory.js')).OpsDirectoryPage }));
const OpsFleetPage = lazy(async () => ({ default: (await import('./pages/ops-fleet.js')).OpsFleetPage }));
const OpsPeoplePage = lazy(async () => ({ default: (await import('./pages/ops-people.js')).OpsPeoplePage }));
const OpsBillingPage = lazy(async () => ({ default: (await import('./pages/ops-billing.js')).OpsBillingPage }));
const OpsCashDeskPage = lazy(async () => ({ default: (await import('./pages/ops-cash-desk.js')).OpsCashDeskPage }));
const OpsKitPage = lazy(async () => ({ default: (await import('./pages/ops-kit.js')).OpsKitPage }));

/**
 * The IP-only staging box has no publicly-trusted certificate and must never carry a real
 * Merchant or a real COD balance (§6 makes custody a regulated concern). A banner is a
 * weak control, but an unlabelled staging environment that looks exactly like production
 * is how real orders end up in one.
 *
 * Off by default, so local development is not shouted at.
 */
function StagingBanner(): React.JSX.Element | null {
  const { t } = useTranslation();
  if (import.meta.env.VITE_STAGING_BANNER !== 'true') return null;
  return (
    <div className="banner no-print" role="status">
      {t('staging.banner')}
    </div>
  );
}

/**
 * Where the header's back link points, or `null` on the two routes that are already the
 * top of their tree.
 *
 * Deliberately a **parent**, not the previous page. A Shipment opened from the board
 * should return to the board whether it was reached by clicking or by pasting a link, and
 * `/orders/:ref/labels` should return to the Order rather than to whatever the browser
 * happens to remember. Nested cases are listed longest-first, because `/orders/x/labels`
 * also matches the `/orders/:ref` shape.
 *
 * **A flat screen now has no parent, and that changed with the rail (ADR-0085).** Every one
 * of these used to return `/`, because the landing page was the only way between Ops screens
 * — two clicks and a round trip. The sidebar is on every page, so a link reading "back" that
 * goes somewhere already visible in the rail is not a second route, it is a second meaning
 * for the same control: a reader who finds it says "the list" on four screens and "home" on
 * twenty-four learns to stop reading it. It appears where it names a **record's** container
 * and nowhere else.
 */
export function parentOf(pathname: string): string | null {
  if (pathname === '/' || pathname === '/login') return null;
  if (/^\/orders\/[^/]+\/labels\/?$/u.test(pathname)) {
    return pathname.replace(/\/labels\/?$/u, '');
  }
  if (/^\/ops\/shipments\/[^/]+\/?$/u.test(pathname)) return '/ops';
  // The tariff editor returns to the Merchant it was opened for, not to the landing page.
  if (/^\/ops\/config\/tariffs\/[^/]+\/new\/?$/u.test(pathname)) {
    return `/ops/config/tariffs?merchant=${pathname.split('/')[4] ?? ''}`;
  }
  if (/^\/orders\/[^/]+\/?$/u.test(pathname)) return '/orders';
  return null;
}

function Header(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { state, signOut } = useSession();
  const navigate = useNavigate();
  const parentPath = parentOf(useLocation().pathname);
  const [ikigaiRemains, setIkigaiRemains] = useState(false);
  const locale = i18n.language as Locale;
  // The language being fetched, if any — see the picker below.
  const [switching, setSwitching] = useState<Locale | null>(null);

  async function handleSignOut(): Promise<void> {
    const remains = await signOut();
    setIkigaiRemains(remains);
    navigate('/login', { replace: true });
  }

  return (
    <>
      <header className="bar">
        <div className="brand">
          <Link to="/" className="brand__home">
            {t('brand')}
          </Link>{' '}
          <small>{t('tagline')}</small>
        </div>
        <div className="bar__actions">
          {/*
            The quick-jump (ADR-0085), before the language switcher: the rail answers "one click"
            for the screens a reader can see at once, and this answers it for the reader holding
            every responsibility, whose rail is longer than a laptop.
          */}
          {state.status === 'authenticated' && <QuickJump session={state.session} />}
          {/*
            Every route below this header was a dead end: the landing page links *into* the
            sixteen screens and nothing linked back out, so the only way home was editing the
            URL. Putting it here rather than in each page means all sixteen inherit it and a
            seventeenth cannot forget it.

            The target is computed from the path, not `navigate(-1)`. History-based back
            leaves the application entirely when a page was opened directly — and these URLs
            get pasted into messages, which is exactly when someone lands mid-tree with no
            history to go back through.

            A word, not an arrow: a glyph has to mirror under RTL, and `tools/rtl-gate` would
            be right to distrust any hand-placed direction. A translated label needs neither
            a transform nor an exception comment.
          */}
          {parentPath !== null && (
            <Link className="btn" to={parentPath}>
              {t('nav.back')}
            </Link>
          )}
          {/*
            `aria-busy` while the chunk is in flight, because since slice 8 a switch to English or
            French fetches its bundle (`i18n/index.ts`). Arabic is bundled and resolves at once.

            Deliberately **not** `disabled`: a disabled button leaves the tab order, so disabling the
            one just clicked would drop a keyboard reader's focus to `<body>` — slice 5 recorded that
            tradeoff for `SubmitButton` and it decides against it here, where the act is cheap and
            repeating it is harmless. `aria-pressed` stays truthful rather than optimistic: it follows
            `languageChanged`, so a failed load leaves the old language both shown and announced.

            Its limit, stated: a *sighted* reader on a slow link sees no indicator for that moment. A
            spinner in a three-character button would need its own copy and its own visual language,
            which is a design decision rather than a bundle one.
          */}
          <nav className="langs" aria-label={t('lang.label')}>
            {LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                className="lang"
                aria-pressed={locale === code}
                aria-busy={switching === code}
                onClick={() => {
                  setSwitching(code);
                  void i18n.changeLanguage(code).finally(() => setSwitching(null));
                }}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </nav>
          {/* The bell (ADR-0078 §6): an Ops person's own alerts, routed to the facets they hold. */}
          {state.status === 'authenticated' && state.session.role === 'opsUser' && <InboxBell />}
          {state.status === 'authenticated' && (
            <button type="button" className="btn" onClick={() => void handleSignOut()}>
              {t('session.signOut')}
            </button>
          )}
        </div>
      </header>

      {/*
        Ikigai has no logout endpoint, so its session outlives ours and signing back in
        will not ask for a password. ADR-0017's consequence, said out loud — a "log out"
        that quietly does half of what the user expects is worse than one labelled honestly.
      */}
      {ikigaiRemains && (
        <p className="notice notice--warn" role="status">
          {t('session.ikigaiRemains')}
        </p>
      )}
    </>
  );
}

/**
 * The rail, for whoever is signed in.
 *
 * Its own component rather than a branch inside `Shell`, because it needs the session and
 * the route table does not: every route below is declared to the router and guarded
 * individually, so reading the session one level up would be a second gate in a place
 * nothing enforces — which is the asymmetry `require-session.tsx`'s own header warns about.
 *
 * It renders nothing at all for an anonymous reader, so `/login` has no rail. That is not a
 * special case here; `reachableGroups` returns nothing for a session that holds nothing.
 */
function ShellRail(): React.JSX.Element | null {
  const { state } = useSession();
  if (state.status !== 'authenticated') return null;
  return <Sidebar session={state.session} />;
}

/**
 * A screen's own heading, from the table that names it (ADR-0085, D3).
 *
 * **Rendered here rather than by each of twenty-seven pages, and that is the decision.** This
 * application had no `<h1>` at all — 102 `<h2>` elements and nothing above them, so every route's
 * heading tree started at level two, which is the outline a screen reader walks to find out where
 * it is. Adopting `PageHeader` page by page would have left the property to thirty-nine separate
 * memories; from here a screen cannot be missing one, and a twenty-eighth gets its heading from
 * the commit that adds it to the table.
 *
 * **Only on an exact match.** A record beneath a screen — an Order, a Shipment, a label sheet —
 * is not the screen, and heading it "Orders" would be a worse answer than heading it nothing. The
 * five record routes still carry no `<h1>`, which is the remainder of D3 and is named in the ADR
 * rather than quietly counted as done: each needs a title only that page knows.
 *
 * The cost, stated: a page's title is no longer written in the page, so `PageHeader`'s `actions`
 * slot is unreachable from one. Nothing uses it yet; a page that needs it renders its own.
 */
function ShellHeading(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { state } = useSession();
  const { pathname } = useLocation();
  if (state.status !== 'authenticated') return null;

  const here = currentScreen(pathname, reachableScreens(state.session));
  if (!here || here.current !== 'page') return null;
  return <PageHeader title={t(here.screen.navKey ?? here.screen.labelKey)} />;
}

/**
 * What a reader sees while a screen's chunk is in flight (slice 8).
 *
 * It sits **below** `ShellHeading`, which is what makes the wait legible rather than blank: the
 * heading comes from the route table and renders outside this boundary, so the page already says
 * which screen it is and only its content is pending. A full-page spinner would throw that away.
 *
 * **It is a conditionally-mounted live region, which `DataTable` names as the unreliable shape**
 * (F18: a `role="status"` that comes and goes with its message is announced inconsistently). It is
 * accepted here with the reason rather than copied in ignorance: the persistent alternative needs
 * suspension lifted into React state, and what a screen reader most needs on a route change — which
 * screen this is — is already outside the boundary and already announced. So the region is a
 * courtesy, and the heading is the control.
 */
function RouteLoading(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <p className="notice" role="status">
      {t('common.loading')}
    </p>
  );
}

function Shell(): React.JSX.Element {
  return (
    <>
      <StagingBanner />
      {/*
        The header moved out of `.shell` with the width change, and the move *is* the width
        change: it sat inside the 60rem column, so widening the page without lifting it would
        have left the brand and the sign-out button floating in the middle of a 1920px screen.
      */}
      {/*
        `no-print` on the chrome, measured rather than assumed (register row 39).
        `base.css`'s print block says in as many words that "everything that is not the label is
        `no-print`" — and it hid only what carried the class, so from the day the rail and the
        topbar shipped, every printed label sheet carried the brand, the language picker, the
        sign-out button, the bell, the quick-jump and the rail's links on 4x6 thermal stock.
        Measured under `emulateMedia('print')`: topbar SHOWN, rail SHOWN, quick-jump SHOWN.

        The class goes on the element rather than into the print block's selector list, because a
        list is what went stale: anything added inside the topbar now inherits it, and
        `tools/visual/built.mjs` holds the property in a browser, which is the only place a
        `@media print` rule can be seen at all.

        `ShellHeading` is deliberately **not** `no-print`: `/statement` prints, and a statement on
        paper wants its title.
      */}
      <div className="topbar no-print">
        <Header />
      </div>
      <div className="layout">
        <ShellRail />
        <main className="shell">
          <ShellHeading />
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <RequireSession>
                    <LandingPage />
                  </RequireSession>
                }
              />
              <Route
                path="/orders"
                element={
                  <RequireSession>
                    <OrdersListPage />
                  </RequireSession>
                }
              />
              <Route
                path="/orders/new"
                element={
                  <RequireSession>
                    <OrderNewPage />
                  </RequireSession>
                }
              />
              <Route
                path="/orders/:reference"
                element={
                  <RequireSession>
                    <OrderDetailPage />
                  </RequireSession>
                }
              />
              {/*
                A Merchant's own remittance statement. Behind `RequireSession` only — it needs
                no platform-staff gate, because the route reads the caller's own tenant from
                their binding and has no id to point elsewhere (M6).
              */}
              <Route
                path="/statement"
                element={
                  <RequireSession>
                    <StatementPage />
                  </RequireSession>
                }
              />
              {/*
                A person's own notification settings (P4-3c). `RequireSession` alone, as `/orders` and
                `/statement` are: the API refuses a Merchant with `@Roles('opsUser')`, and the route
                table's `audience: 'ops'` is what keeps a Merchant from being offered the link —
                authority and audience being separate fields is exactly this case.
              */}
              <Route
                path="/me/notifications"
                element={
                  <RequireSession>
                    <MeNotificationsPage />
                  </RequireSession>
                }
              />
              {/*
                The Ops console. `RequirePlatformStaff` sits inside `RequireSession` so an
                anonymous visitor still meets the login page rather than a silent redirect to
                a landing page they cannot see either.
              */}
              <Route
                path="/ops"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsBoardPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/couriers"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsCouriersPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/audit"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsAuditPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/credentials"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsCredentialsPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/exceptions"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsExceptionsPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/sla-breaches"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsSlaBreachesPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/orders/:reference/labels"
                element={
                  <RequireSession>
                    <OrderLabelsPage />
                  </RequireSession>
                }
              />
              <Route
                path="/ops/compliance"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsCompliancePage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/dispatch"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsDispatchPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/shipments/:shipmentId"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsShipmentDetailPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/config/policy"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="policy">
                      <OpsConfigPolicyPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              {/* The screening policy is the compliance facet's alone, as its route is: the table is
                  platform-scoped, so no platform staff and no justification (ADR-0071 §4, SC2c). */}
              <Route
                path="/ops/config/screening-policy"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="compliance">
                      <OpsConfigScreeningPolicyPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              {/* The licence to hold Buyers' cash is compliance's alone, as its route is (ADR-0077). */}
              <Route
                path="/ops/config/cod-custody-licence"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="compliance">
                      <OpsConfigCodCustodyLicencePage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              {/* How long a screening stays current is compliance's alone, as its route is (P2-8b). */}
              <Route
                path="/ops/config/screening-validity"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="compliance">
                      <OpsConfigScreeningValidityPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              {/* The notification rules are the notifications facet's alone, as their routes are (ADR-0078). */}
              <Route
                path="/ops/notifications"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="notifications">
                      <OpsNotificationsPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              {/*
                The sends log (P4-3d, ADR-0078 §11): platform staff for the crossing, `notifications`
                for the area. A send belongs to one Merchant where a rule belongs to none, which is
                why this is its own route rather than a tab on the screen above — folding them would
                put the rules behind a justification gate. The API refuses on both independently;
                these are navigation courtesies.
              */}
              <Route
                path="/ops/notifications/sends"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <RequireOpsFacet facet="notifications">
                        <OpsNotificationSendsPage />
                      </RequireOpsFacet>
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              {/* The rail Merchants are paid over is finance's alone, as its route is (ADR-0066, P2-1). */}
              <Route
                path="/ops/config/remittance-rail"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="finance">
                      <OpsConfigRemittanceRailPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/config/fx-rates"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="finance">
                      <OpsConfigFxPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/config/vat"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="finance">
                      <OpsConfigVatPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              {/* The platform's VAT registration, which a taxed invoice names, is finance's alone (ADR-0082 §5). */}
              <Route
                path="/ops/config/vat-registration"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="finance">
                      <OpsConfigVatRegistrationPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/geography"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="geography">
                      <OpsGeographyPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/catalogue"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="catalogue">
                      <OpsCataloguePage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/directory"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="directory">
                      <OpsDirectoryPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/fleet"
                element={
                  <RequireSession>
                    <RequireOpsFacet facet="fleet">
                      <OpsFleetPage />
                    </RequireOpsFacet>
                  </RequireSession>
                }
              />
              {/*
                The people console (ADR-0067): `access` or platform staff, the one disjunction among
                the gates — platform staff alone grant `access`, so they must reach the screen that
                does it. The API refuses independently; this is a navigation courtesy.
              */}
              <Route
                path="/ops/people"
                element={
                  <RequireSession>
                    <RequireAccessAuthority>
                      <OpsPeoplePage />
                    </RequireAccessAuthority>
                  </RequireSession>
                }
              />
              {/*
                A Merchant's rate guide: platform staff for the crossing, `pricing` for the area
                (ADR-0061). The API refuses on both independently; these are navigation courtesies.
              */}
              <Route
                path="/ops/config/tariffs"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <RequireOpsFacet facet="pricing">
                        <OpsConfigTariffsPage />
                      </RequireOpsFacet>
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route
                path="/ops/config/tariffs/:merchantId/new"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <RequireOpsFacet facet="pricing">
                        <OpsConfigTariffEditorPage />
                      </RequireOpsFacet>
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              {/*
                A Merchant's billing: their commercial terms and the invoices issued under them
                (ADR-0066). Platform staff for the crossing, `finance` for the area, exactly as the
                tariff screen above — the API refuses on both independently and these are navigation
                courtesies. `finance` rather than `pricing`, because what a Merchant is charged is a
                price and when it falls due is money's own rule (migration 0029).
              */}
              <Route
                path="/ops/billing"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <RequireOpsFacet facet="finance">
                        <OpsBillingPage />
                      </RequireOpsFacet>
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              {/*
                The cash desk (P2-7, ADR-0083): platform staff for the crossing, `hub_finance` for the
                area — the officer who counts a Courier's cash in and banks the drawer out. The API refuses
                on both independently; these are navigation courtesies.
              */}
              <Route
                path="/ops/cash-desk"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <RequireOpsFacet facet="hub_finance">
                        <OpsCashDeskPage />
                      </RequireOpsFacet>
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              {/*
                The component gallery (ADR-0084 §6). Platform staff, and no facet: it renders fixture
                data and reaches no tenant, so there is nothing for a facet to be about. It is gated
                at all because it is not a screen an operator has any business finding — and it is a
                route inside the real application rather than a Storybook iframe precisely so that it
                inherits the real stylesheet, fonts, i18n and `dir`.
              */}
              <Route
                path="/ops/_kit"
                element={
                  <RequireSession>
                    <RequirePlatformStaff>
                      <OpsKitPage />
                    </RequirePlatformStaff>
                  </RequireSession>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </>
  );
}

export function App(): React.JSX.Element {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}
